// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * A 64-bit perceptual hash held as two unsigned 32-bit halves.
 *
 * Not a BigInt: the all-pairs Hamming pass is the only quadratic step in the tool, and BigInt
 * XOR allocates. Not a Uint8Array either — a pair of numbers keeps the popcount in registers.
 */
export type DHash = readonly [hi: number, lo: number];

/** Why an image is not in the export set. `kept` means it is. */
export type Verdict = 'kept' | 'duplicate' | 'blurred' | 'excluded';

export interface ImageRecord {
  /** Stable index in ingestion order; also the id used in embeddings.f32. */
  readonly id: number;
  /** Path as the user supplied it (relative inside a folder or zip). Never trusted as a filesystem path. */
  readonly path: string;
  readonly name: string;
  readonly bytes: number;
  readonly type: string;
  readonly width: number;
  readonly height: number;
  readonly hash: DHash;
  /** Variance of the Laplacian over a fixed 256x256 luminance grid. Comparable ONLY within a group. */
  readonly sharpness: number;
  /** Mean absolute Laplacian response — used with `sharpness` to spot flat, screenshot-like frames. */
  readonly edgeDensity: number;
  /** Share of pixels that are exactly one of the six most common colours. High => synthetic/UI. */
  readonly flatness: number;
}

export interface Group {
  /** Group index, stable across a run. */
  readonly id: number;
  /** Member image ids, ascending. Always at least one. */
  readonly members: readonly number[];
  /** The member chosen to represent the group in the export. */
  readonly keeper: number;
  /** How the members were joined: perceptual hash, embedding cosine, or both. */
  readonly via: 'hash' | 'embedding' | 'both';
}

export interface Decision {
  readonly id: number;
  readonly verdict: Verdict;
  /** Human-readable reason, e.g. 'near-duplicate of img_001.jpg (hamming 3)'. */
  readonly reason: string;
  /** The image this one was judged against, or null for a keeper or a standalone exclusion. */
  readonly against: number | null;
  readonly groupId: number | null;
}

export interface Thresholds {
  /**
   * Max Hamming distance between 64-bit dHashes for two frames to be treated as the same picture.
   *
   * THE ONLY SIGNAL ALLOWED TO SET ASIDE AN IMAGE ON ITS OWN. Measured on a 158-image library of
   * hand-curated, genuinely distinct photographs (the DreamBooth reference subjects), a threshold
   * of 5 produced zero false matches across all 12,403 pairs.
   */
  readonly hamming: number;
  /**
   * Min cosine similarity for two images to be OFFERED for review as look-alikes.
   *
   * Never a deletion rule — see `similar.ts` for the measurements that rule it out.
   */
  readonly cosine: number;
  /**
   * Drop a frame whose sharpness is below this FRACTION of its group's best.
   * Relative, never absolute: variance of the Laplacian has no meaning across different scenes.
   */
  readonly blurRatio: number;
  /** Drop frames that look like flat UI rather than photographs. */
  readonly excludeFlat: boolean;
}

export const DEFAULT_THRESHOLDS: Thresholds = {
  hamming: 5,
  cosine: 0.95,
  blurRatio: 0.5,
  excludeFlat: false,
};

export interface DatasetOptions {
  /** kohya_ss repeat count — the number in `10_myconcept`. */
  readonly repeats: number;
  /** kohya_ss concept/class name — the word after the underscore. */
  readonly concept: string;
  /** Written into every caption file and into metadata.jsonl. Empty means no caption files. */
  readonly caption: string;
}
